import { TFunction } from "react-i18next";
import { CompanyPage, CompanyPageBid } from "../../../../hooks/slices/companySlice";
import { CompanyDetailsContentItem } from "../../../../types/company";

// Type Definitions (Copied from index.tsx or inferred)
export interface parsedAccreditationAttributes {
    codeLetter: string;
    codeNumber: number;
    codeLetterDescription: string;
    codeNumberDescription: string;
    class: number;
}

export interface AccreditationsDataStructure {
    letters: LetterDataStructure[];
}
export interface LetterDataStructure {
    letter: string;
    description: string;
    classes: number[];
    numbers: NumbersDataStructure[];
}
export interface NumbersDataStructure {
    number: number;
    description: string;
    classes: number[];
}
export interface AccreditationGraphData {
    x: string;
    y: number[];
    label: string[];
}

export interface SimpleYoYSubtitleResult {
    changeDescription: string;
    trendIcon: '↑' | '↓' | '';
    colorSuffix: 'up' | 'down' | 'none';
}

interface SimpleYoYSubtitlePropsArgs {
    yearOverYearChange: number;
    t: TFunction;
    increaseKey: string;
    decreaseKey: string;
    noChangeKey: string;
}


// Helper Functions

/**
 * Calculates a specific metric for each year based on grouped bids.
 * Requires specifying the years of interest to ensure all are included, even if no bids exist.
 */
export function calculateMetricByYear<T>(
    yearlyBids: Record<string, CompanyPageBid[]>,
    years: number[],
    calculateMetric: (bidsInYear: CompanyPageBid[]) => T,
    defaultValue: T
): Record<string, T> {
    const metrics: Record<string, T> = {};
    for (const year of years) {
        const yearStr = year.toString();
        const bidsInYear = yearlyBids[yearStr] ?? [];
        metrics[yearStr] = bidsInYear.length > 0 ? calculateMetric(bidsInYear) : defaultValue;
    }
    return metrics;
}

/**
 * Calculates the Year-over-Year change between two numeric metrics.
 * Handles undefined/null inputs and division by zero for percentage change.
 */
export function calculateYoYChange(
    currentMetric: number | undefined | null,
    lastMetric: number | undefined | null,
    changeType: 'percentagePoints' | 'percentageChange'
): number | null {
    if (currentMetric === null || currentMetric === undefined || lastMetric === null || lastMetric === undefined) {
        return null;
    }

    if (changeType === 'percentagePoints') {
        return currentMetric - lastMetric;
    } else { // percentageChange
        if (lastMetric === 0) {
            return currentMetric > 0 ? 100 : 0;
        }
        return ((currentMetric - lastMetric) / lastMetric) * 100;
    }
}


/**
 * Groups bids by year based on awardDate or deadline.
 */
export function reduceBidsByYear(bids: CompanyPageBid[]): Record<string, CompanyPageBid[]> {
    return bids.reduce((acc: Record<string, CompanyPageBid[]>, bid) => {
        const dateToUse = bid.awardDate ?? bid.deadline;
        if (dateToUse) {
            const year = new Date(dateToUse).getFullYear().toString();
            if (!acc[year]) acc[year] = [];
            acc[year].push(bid);
        }
        return acc;
    }, {});
}

/**
 * Parses a single accreditation string into structured data.
 */
export function parseAccreditation(
    accreditation: string,
    t: TFunction,
): parsedAccreditationAttributes {
    const parts = accreditation.split('-');
    const code = parts[0]?.trim() ?? '';
    const classStr = parts[1]?.trim() ?? '0';

    const codeLetter = code[0] ?? '';
    const codeNumberStr = code.substring(1);
    const codeNumber = codeNumberStr ? Number(codeNumberStr) : 0;

    const codeLetterDescription = t(`codes.code_${codeLetter}`);
    const codeNumberDescription = (codeNumber > 0)
        ? t(`codes.code_${codeLetter}${codeNumber}`)
        : t(`codes.code_${codeLetter}`);

    const class_ = Number(classStr);

    return {
        codeLetter,
        codeNumber,
        codeLetterDescription,
        codeNumberDescription,
        class: class_,
    };
}

/**
 * Transforms a list of parsed accreditations into a nested data structure by letter and number.
 */
export function parsedAccreditationListToDataStructure(
    parsedAccreditations: parsedAccreditationAttributes[],
): AccreditationsDataStructure {

    const accreditationByLetter = parsedAccreditations.reduce((acc, parsedAccreditation) => {
        const codeLetter = parsedAccreditation.codeLetter;
        if (!acc[codeLetter]) acc[codeLetter] = [];
        acc[codeLetter].push(parsedAccreditation);
        return acc;
    }, {} as Record<string, parsedAccreditationAttributes[]>);

    const lettersData = Object.entries(accreditationByLetter).map(([codeLetter, accreditationsInLetter]) => {
        const numbersByCode = accreditationsInLetter.reduce((acc, pa) => {
            const codeNumber = pa.codeNumber;
            if (!acc[codeNumber]) acc[codeNumber] = { number: codeNumber, description: pa.codeNumberDescription, classes: [] };
            if (!acc[codeNumber].classes.includes(pa.class)) {
                 acc[codeNumber].classes.push(pa.class);
            }
             acc[codeNumber].classes.sort((a, b) => a - b); // Ensure classes are sorted
            return acc;
        }, {} as Record<string, { number: number; description: string; classes: number[] }>);

        const overallClasses = Array.from(new Set(accreditationsInLetter.map(pa => pa.class))).sort((a, b) => a - b);

        // Filter out the "0" category if it's the only one and contains the same classes as the main letter
        let numbersArray = Object.values(numbersByCode);
        if (numbersArray.length === 1 && numbersArray[0].number === 0 && JSON.stringify(numbersArray[0].classes) === JSON.stringify(overallClasses)) {
            numbersArray = [];
        }


        return {
            letter: codeLetter,
            description: accreditationsInLetter[0].codeLetterDescription, // Assuming all descriptions for the same letter are identical
            classes: overallClasses,
            numbers: numbersArray,
        };
    });

    return { letters: lettersData };
}


/**
 * Formats accreditation data for the summary bar graph.
 */
export function parseAccreditationForSummaryGraph(
    accreditations: AccreditationsDataStructure,
    accreditationsMainCategories: string[],
    t: TFunction,
): AccreditationGraphData[] {
    let data: AccreditationGraphData[] = accreditationsMainCategories.map((mainCategory) => {
        const mainCategoryDataStructure = accreditations.letters.find(letter => letter.letter === mainCategory);
        const maxY = mainCategoryDataStructure && mainCategoryDataStructure.classes.length > 0
            ? Math.max(...mainCategoryDataStructure.classes)
            : 0;
        return {
            x: mainCategory,
            y: [maxY], // Use max class for the category
            label: [`${t(`codes.code_${mainCategory}`)} | class`],
        }
    });
    return data;
}

/**
 * Formats accreditation data for the detailed bar graph.
 */
export function parseAccreditationForDetailsGraph(
    accreditations: AccreditationsDataStructure,
    accreditationsSubCategories: string[], // Expects full codes like 'A1', 'B2' etc.
    t: TFunction,
): AccreditationGraphData[] {
     let data: AccreditationGraphData[] = accreditationsSubCategories.map((subCategoryCode) => {
        const letter = subCategoryCode[0];
        const numberStr = subCategoryCode.slice(1);
        const number = numberStr ? Number(numberStr) : 0; // Treat 'A' as 'A0'

        let maxY = 0;
        const letterData = accreditations.letters.find(l => l.letter === letter);

        if (letterData) {
            if (number === 0) {
                 // If it's a main category code like 'A', use its max class if no subcategories exist or handle specifically
                 // Based on original logic, if numbers is empty and number is 0, use letter's max class
                 if (letterData.numbers.length === 0) {
                      maxY = letterData.classes.length > 0 ? Math.max(...letterData.classes) : 0;
                 }
            } else {
                const numberData = letterData.numbers.find(n => n.number === number);
                if (numberData) {
                    maxY = numberData.classes.length > 0 ? Math.max(...numberData.classes) : 0;
                }
            }
        }

        return {
            x: subCategoryCode,
            y: [maxY],
            label: [`${t(`codes.code_${subCategoryCode}`)} | class`], // Use the full subcategory code for translation
        };
    });

    // Filter out entries where y is 0? Optional, depending on desired graph behavior.
    // data = data.filter(d => d.y[0] > 0);

    return data;
}


/**
 * Generates properties for a simple Year-over-Year subtitle based on change.
 */
export function getSimpleYoYSubtitleProps({
    yearOverYearChange,
    t,
    increaseKey,
    decreaseKey,
    noChangeKey,
}: SimpleYoYSubtitlePropsArgs): SimpleYoYSubtitleResult | null {

    let changeDescription: string;
    let trendIcon: '↑' | '↓' | '';
    let colorSuffix: 'up' | 'down' | 'none';

    if (yearOverYearChange > 0) {
        changeDescription = t(increaseKey);
        trendIcon = '↑';
        colorSuffix = 'up';
    } else if (yearOverYearChange < 0) {
        changeDescription = t(decreaseKey);
        trendIcon = '↓';
        colorSuffix = 'down';
    } else {
        changeDescription = t(noChangeKey);
        trendIcon = ''; // No icon for no change
        colorSuffix = 'none';
    }

    // Decide if null should be returned for 0 change - keeping original behavior
    // if (yearOverYearChange === 0) return null;

    return {
        changeDescription,
        trendIcon,
        colorSuffix,
    };
}


// --- Add getCompanyAccreditations ---
export function getCompanyAccreditations(company: CompanyPage): CompanyDetailsContentItem[]{
    const accreditations: string[] = [...company.accreditations ?? []];
    //the accreditations are temporarily scattered in two places: the accreditations array and the activities array
    company.activities.filter(a => a.type === "ACCREDITATION").forEach(a => {
        if (a.code && !accreditations.includes(a.code)) {
            accreditations.push(a.code);
        }
    });
    if (accreditations.length > 0) {
        accreditations.sort(); // Sort alphabetically/numerically
        return accreditations.map(activity => ({ value: activity }))
    } else return [];
} 